import { useState } from 'react'
import { ProfilePhotoUpload } from '@/components/user-profile/ProfilePhotoUpload'
import { UserDetailsForm } from '@/components/user-profile/UserDetailsForm'
import { Button } from '@/components/ui/button'
import { Menu } from 'lucide-react'
import { useSidebar } from '@/components/ui/sidebar'
import { tokenService } from '@/lib/token.service'
import type { User } from '@/schemas/user.schema'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AUTH_QUERY_KEYS } from '@/api/queryKeys'
import axios from 'axios'
import { showErrorToast, showSuccessToast } from '@/components/ui/toast'
import {
  getUploadUrl,
  updateUserProfile,
  type UpdateUserProfilePayload,
} from '@/api/userApi'
import { useUserRole } from '@/contexts/UserRoleContext'
import {
  convertToChatUserPayload,
  updateChatUserCache,
} from '@/utils/chatUtils'

function UserProfile() {
  const [profilePhoto, setProfilePhoto] = useState<File | null>(null)
  const [removedPhoto, setRemovedPhoto] = useState(false)
  const [isUploading, setUploading] = useState(false)
  const queryClient = useQueryClient()
  const { updateUser } = useUserRole()

  const hasPhotoChanged = profilePhoto !== null || removedPhoto

  const handlePhotoChange = (file: File | null) => {
    setProfilePhoto(file)
    setRemovedPhoto(false)
  }

  const handleRemove = () => {
    setRemovedPhoto(true)
    setProfilePhoto(null)
  }

  const user: User = tokenService.getUser()

  const { mutateAsync, isPending } = useMutation({
    mutationFn: updateUserProfile,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [AUTH_QUERY_KEYS.userProfile],
      })

      showSuccessToast(
        'Profile Updated',
        `Your profile details have been successfully saved.`
      )
    },
    onError: error => {
      if (axios.isAxiosError(error)) {
        const message =
          error?.response?.data?.message || 'Failed to update profile details'
        showErrorToast('Profile update Failed', message)
      }
    },
  })

  const transformFormData = async (data: User) => {
    let profilePictureKey = ''

    if (profilePhoto instanceof File) {
      setUploading(true)
      try {
        const [uploadData] = await getUploadUrl({
          presignPayload: [{ linkFor: 'profile', fileType: profilePhoto.type }],
        })
        if (!uploadData?.url || !uploadData.key) {
          throw new Error('Could not generate upload URL')
        }

        await axios.put(uploadData.url, profilePhoto, {
          headers: {
            'Content-Type': profilePhoto.type,
            'x-ms-blob-type': 'BlockBlob',
          },
        })

        profilePictureKey = uploadData.key
      } catch (err) {
        console.error('Image upload failed:', err)
        throw err
      } finally {
        setUploading(false)
      }
    }

    const picturePayload: { profilePicture?: string } = {}
    if (profilePhoto instanceof File) {
      picturePayload.profilePicture = profilePictureKey
    } else if (!removedPhoto && user.profilePicture?.key) {
      picturePayload.profilePicture = user.profilePicture.key
    }

    return {
      firstName: data.firstName,
      lastName: data.lastName,
      phoneNumber: data.phoneNumber,
      ...picturePayload,
      address: {
        street: data.address?.street ?? '',
        city: data.address?.city ?? '',
        state: data.address?.state ?? '',
        country: data.address?.country ?? '',
        zipCode: data.address?.zipCode ?? '',
      },
      ...(data.gender?.trim() && { gender: data.gender }),
      ...(data.birthDate?.trim() && { birthDate: data.birthDate }),
    } as UpdateUserProfilePayload
  }

  const handleFormSubmit = async (data: User) => {
    try {
      const payload = await transformFormData(data)
      const response = await mutateAsync(payload)
      const updated = response.data?.data
      if (updated) {
        tokenService.saveUser(updated)
        updateUser(updated)
        await updateChatUserCache(
          updated?.id,
          convertToChatUserPayload(updated)
        )
        setProfilePhoto(null)
        setRemovedPhoto(false)
      }
    } catch (err) {
      console.error('Form submission error:', err)
    }
  }

  const handleCancel = () => {
    setProfilePhoto(null)
    setRemovedPhoto(false)
  }

  const { toggleSidebar } = useSidebar()

  return (
    <div className="bg-white/78 backdrop-blur-sm rounded-none lg:rounded-2xl border border-white/80 shadow-sm min-h-screen lg:min-h-full overflow-hidden flex-1 flex flex-col">
      {/* Header Section with Background Image and Profile */}
      <div className="relative">
        {/* Header Background Image */}
        <div
          className="h-60 bg-cover bg-center bg-no-repeat relative"
          style={{
            backgroundImage: `url('/auth/banner-img.png')`,
          }}
        >
          <div className="absolute inset-0 bg-black/20" />

          <div className="flex items-center gap-3 flex-1 min-w-0 absolute left-4">
            {/* Hamburger Menu - Visible from lg breakpoint and below */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                toggleSidebar()
              }}
              className="lg:hidden size-10 p-0"
              aria-label="Open sidebar"
            >
              <Menu className="size-8 text-ss-neutral-950" aria-hidden="true" />
            </Button>
          </div>
        </div>

        {/* Profile Photo and Info */}
        <div className="flex flex-col sm:flex-row items-start sm:items-end gap-0 sm:gap-6 -mt-12 px-8 pb-0 lg:pb-6">
          <div className="relative">
            <ProfilePhotoUpload
              currentPhotoUrl={user.profilePicture?.url}
              selectedFile={profilePhoto}
              removed={removedPhoto}
              onPhotoChange={handlePhotoChange}
              onRemove={handleRemove}
              size="lg"
              className="mb-2"
            />
          </div>

          {/* User Info */}
          <div className="flex-1 pt-0 sm:pt-16">
            <div className="mb-5">
              <h1 className="text-3xl font-bold text-ss-neutral-950 leading-9 mb-1">
                {user.firstName} {user.lastName}
              </h1>
              <p className="text-base font-medium text-ss-neutral-600 leading-6">
                {user.email}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Form Section */}
      <UserDetailsForm
        initialData={user}
        onSubmit={handleFormSubmit}
        onCancel={handleCancel}
        isLoading={isPending}
        isUploading={isUploading}
        hasPhotoChanged={hasPhotoChanged}
      />
    </div>
  )
}

export default UserProfile
